import {lazy, Suspense} from 'react';
import {
    Route, Navigate, Outlet, RouterProvider,
    createBrowserRouter, createRoutesFromElements
} from 'react-router-dom';

import Login from "./features/auth/Login";

import Layout from "./components/Layout";
import RequireAuth from "./features/auth/RequireAuth";
import RequireRole from "./features/auth/RequireRole";

import useInsideURLs from "./hooks/useInsideURLs";
import Loader from "./UI/loader/Loader";

import CheckAuth from "./features/auth/CheckAuth";
import BackgroundForModal from "./components/modal/BackgroundForModal";

const Dashboard = lazy(() => import("./features/dashboard/Dashboard"));
const ResourceFormRoute = lazy(() => import("./features/crud/ResourceFormRoute"));
const HeaderFooterRoute = lazy(() => import("./features/mailing/HeaderFooterRoute"));
const SubscriberListFormRoute = lazy(() => import("./features/mailing/subscriber_lists/SubscriberListFormRoute"));

const ArticleList = lazy(() => import("./features/website/articles/ArticleList"));
const ArticleForm = lazy(() => import("./features/website/articles/ArticleForm"));
const SchoolList = lazy(() => import("./features/website/schools/SchoolList"));
const SchoolForm = lazy(() => import("./features/website/schools/SchoolForm"));
const UniversityList = lazy(() => import("./features/website/universities/UniversityList"));
const UniversityForm = lazy(() => import("./features/website/universities/UniversityForm"));
const CourseList = lazy(() => import("./features/website/courses/CourseList"));
const CourseForm = lazy(() => import("./features/website/courses/CourseForm"));
const TestList = lazy(() => import("./features/testing/languageLevelTests/TestList"));
const TestForm = lazy(() => import("./features/testing/languageLevelTests/TestForm"));
const ProductList = lazy(() => import("./features/testing/products/ProductList"));
const ProductForm = lazy(() => import("./features/testing/products/ProductForm"));
const TestPageList = lazy(() => import("./features/testing/page/TestPageList"));
const TestPageForm = lazy(() => import("./features/testing/page/TestPageForm"));
const OfflineConversionsPage = lazy(() => import("./features/marketing/offlineConversions/OfflineConversionsPage"));
const UserList = lazy(() => import("./features/website/users/UserList"));
const UserForm = lazy(() => import("./features/website/users/UserForm"));
const ClientList = lazy(() => import("./features/website/clients/ClientList"));
const ClientForm = lazy(() => import("./features/website/clients/ClientForm"));
const RequestList = lazy(() => import("./features/website/requests/RequestList"));
const RequestForm = lazy(() => import("./features/website/requests/RequestForm"));
const CandidateList = lazy(() => import("./features/website/candidates/CandidateList"));
const CandidateForm = lazy(() => import("./features/website/candidates/CandidateForm"));
const ErrorList = lazy(() => import("./features/website/errors/ErrorList"));
const ErrorDetail = lazy(() => import("./features/website/errors/ErrorDetail"));
const NotificationLeadList = lazy(() => import("./features/notification_bot/NotificationLeadList"));
const NotificationLeadDetail = lazy(() => import("./features/notification_bot/NotificationLeadDetail"));
const ChatArchiveList = lazy(() => import("./features/chat/ChatArchiveList"));
const ChatArchiveDetail = lazy(() => import("./features/chat/ChatArchiveDetail"));
const ChatBannedList = lazy(() => import("./features/chat/ChatBannedList"));
const ChatPrompts = lazy(() => import("./features/chat/ChatPrompts"));
const Banners = lazy(() => import("./features/banners/Banners"));
const EssayList = lazy(() => import("./features/testing/essay/EssayList"));
const EssayForm = lazy(() => import("./features/testing/essay/EssayForm"));
const EssayWorkPlaceList = lazy(() => import("./features/testing/essay/EssayWorkPlaceList"));
const EssayWorkPlaceConversation = lazy(() => import("./features/testing/essay/EssayWorkPlaceConversation"));
const LanguageList = lazy(() => import("./features/testing/languages/LanguageList"));
const GlobalSaleList = lazy(() => import("./features/testing/sales/GlobalSaleList"));
const GlobalSaleForm = lazy(() => import("./features/testing/sales/GlobalSaleForm"));
const OrderWorkPlaceList = lazy(() => import("./features/testing/order/OrderWorkPlaceList"));
const OrderWorkPlaceForm = lazy(() => import("./features/testing/order/OrderWorkPlaceForm"));
const UserRequestList = lazy(() => import("./features/testing/userRequests/UserRequestList"));
const UserRequestForm = lazy(() => import("./features/testing/userRequests/UserRequestForm"));
const MainTestCategoryList = lazy(() => import("./features/testing/main_test_categories/MainTestCategoryList"));
const MainTestCategoryForm = lazy(() => import("./features/testing/main_test_categories/MainTestCategoryForm"));
const Cat4TestForm = lazy(() => import("./features/testing/cat4/Cat4TestForm"));
const Cat4TestList = lazy(() => import("./features/testing/cat4/Cat4TestList"));
const PackList = lazy(() => import("./features/testing/pack/PackList"));
const PackForm = lazy(() => import("./features/testing/pack/PackForm"));
const SampleList = lazy(() => import("./features/testing/sample/SampleList"));
const SampleForm = lazy(() => import("./features/testing/sample/SampleForm"));
const TestArticleForm = lazy(() => import("./features/testing/articles/TestArticleForm"));
const TestArticleList = lazy(() => import("./features/testing/articles/TestArticleList"));
const ResultsForAdmins = lazy(() => import("./features/testing/results/ResultsForAdmins"));
const Cat4ResultDetailed = lazy(() => import("./features/testing/results/Cat4ResultDetailed"));
const LanguageLevelResultDetailed = lazy(() => import("./features/testing/results/LanguageLevelResultDetailed"));
const RequestPriceList = lazy(() => import("./features/testing/requestPrices/RequestPriceList"));
const RequestPriceDetailed = lazy(() => import("./features/testing/requestPrices/RequestPriceDetailed"));
const CheckList = lazy(() => import("./features/testing/check/CheckList"));
const CheckDetailed = lazy(() => import("./features/testing/check/CheckDetailed"));
const UniversityPrepProgrammeList = lazy(() => import("./features/website/universityPrepProgramm/UniversityPrepProgrammeList"));
const UniversityPrepProgrammeForm = lazy(() => import("./features/website/universityPrepProgramm/UniversityPrepProgrammeForm"));
const AiProductList = lazy(() => import("./features/testing/ai/AiProductList"));
const AiProductForm = lazy(() => import("./features/testing/ai/AiProductForm"));
const OnlinePlacementTestList = lazy(() => import("./features/testing/iStudyOnlinePlacementTest/OnlinePlacementTestList"));
const OnlinePlacementTestForm = lazy(() => import("./features/testing/iStudyOnlinePlacementTest/OnlinePlacementTestForm"));
const OnlinePlacementTestQuestionList = lazy(() => import("./features/testing/iStudyOnlinePlacementTest/OnlinePlacementTestQuestionList"));
const OnlinePlacementTestQuestionForm = lazy(() => import("./features/testing/iStudyOnlinePlacementTest/OnlinePlacementTestQuestionForm"));
const CategoryList = lazy(() => import("./features/website/categories/CategoryList"));
const CategoryForm = lazy(() => import("./features/website/categories/CategoryForm"));
const TagList = lazy(() => import("./features/website/tags/TagList"));
const TagForm = lazy(() => import("./features/website/tags/TagForm"));
const LocationList = lazy(() => import("./features/website/locations/LocationList"));
const CandidateTypeList = lazy(() => import("./features/website/candidate_types/CandidateTypeList"));
const ProgramList = lazy(() => import("./features/website/programs/ProgramList"));
const RoleList = lazy(() => import("./features/website/roles/RoleList"));
const CountryList = lazy(() => import("./features/website/countries/CountryList"));
const CountryForm = lazy(() => import("./features/website/countries/CountryForm"));
const FilePageUpload = lazy(() => import("./features/website/files/FilePageUpload"));
const ResidenceList = lazy(() => import("./features/website/residences/ResidenceList"));
const GenderInformationForSchoolList = lazy(() => import("./features/website/gender_information_for_school/GenderInformationForSchoolList"));
const GenderInformationForSchoolForm = lazy(() => import("./features/website/gender_information_for_school/GenderInformationForSchoolForm"));
const SchoolTypeList = lazy(() => import("./features/website/school_types/SchoolTypeList"));
const CelebrityList = lazy(() => import("./features/website/celebrities/CelebrityList"));
const CelebrityForm = lazy(() => import("./features/website/celebrities/CelebrityForm"));
const FacultyList = lazy(() => import("./features/website/faculties/FacultyList"));
const VideoList = lazy(() => import("./features/website/videos/VideoList"));
const VideoForm = lazy(() => import("./features/website/videos/VideoForm"));
const TestTypeList = lazy(() => import("./features/testing/test_types/TestTypeList"));
const TestUserList = lazy(() => import("./features/testing/test_users/TestUserList"));
const TestUsersDetailed = lazy(() => import("./features/testing/test_users/TestUsersDetailed"));
const ResultPageForAdmins = lazy(() => import("./features/testing/results_for_admin/ResultPageForAdmins"));
const MailingTypeList = lazy(() => import("./features/mailing/mailing_type/MailingTypeList"));
const HeaderList = lazy(() => import("./features/mailing/header/HeaderList"));
const FooterList = lazy(() => import("./features/mailing/footer/FooterList"));
const EmailMessageList = lazy(() => import("./features/mailing/email_messages/EmailMessageList"));
const EmailMessageForm = lazy(() => import("./features/mailing/email_messages/EmailMessageForm"));
const SubscriberList = lazy(() => import("./features/mailing/subscribers/SubscriberList"));
const SubscriberForm = lazy(() => import("./features/mailing/subscribers/SubscriberForm"));
const SubscriberListList = lazy(() => import("./features/mailing/subscriber_lists/SubscriberListList"));
const MailingList = lazy(() => import("./features/mailing/mailing/MailingList"));
const MailingForm = lazy(() => import("./features/mailing/mailing/MailingForm"));
const MailLogList = lazy(() => import("./features/mailing/maillog/MailLogList"));
const QueueList = lazy(() => import("./features/mailing/queuemail/QueueList"));
const QueueHistoryList = lazy(() => import("./features/mailing/queuemail/QueueHistoryList"));
const SenderList = lazy(() => import("./features/mailing/sender/SenderList"));
const SenderForm = lazy(() => import("./features/mailing/sender/SenderForm"));



const urls = useInsideURLs;

const {
      onlinePlacementTestQuestions,
      onlinePlacementTests,
      queue,
      maillogs,
      subscribers,
      languageLevelTests,
      users,
      leads,
      clients,
      articles,
      categories,
      candidatetypes,
      tags,
      locations,
      programs,
      roles,
      countries,
      candidates,
      files,
      residences,
      genderschoolinformations,
      schools,
      celebrities,
      faculties,
      universities,
      testUsers,
      testUsersInformation,
      mailingTypes,
      headers,
      footers,
      emailMessages,
      subscriberList,
      mailings,
      queueHistory,
      sender,
      videos,
      testTypes,
      essays,
      products,
      languages,
      globalSales,
      orders,
      essayWorkPlace,
      essayConversation,
      userRequests,
      mainCategoryTests,
      cat4tests,
      samples,
      packs,
      testArticles,
      results,
      requestPrices,
      checks,
      universityPrepProgrammes,
      testPages,
      aiProducts,
      courses
} = urls();

const AppFrame = () => (
    <div className="App">
        <Outlet/>
        <BackgroundForModal/>
    </div>
);

const routeTree = (
    <Route element={<AppFrame/>}>
        <Route
            path="/"
            element={<Layout />}
        >
            <Route index element={<CheckAuth />}/>
            {/* Public routes */}
            <Route path="login" element={<Login />}/>
            {/* Protected routes */}
            <Route element={<RequireAuth/>}>
                <Route path={testArticles}>
                    <Route index element=
                        {
                            <TestArticleList
                                label="Статьи"
                                bigLabel="Статью"
                                target={testArticles}
                            />
                        }
                    />
                    <Route path="get/:id" element={<TestArticleForm />}/>
                </Route>
                <Route path={testPages}>
                    <Route index element=
                        {
                            <TestPageList
                                label="Страницы"
                                bigLabel="Страницу"
                                target={testPages}
                            />
                        }
                    />
                    <Route path="get/:id" element={<TestPageForm />}/>
                </Route>

                <Route path={essayConversation}>
                    <Route path="get/:id" element={<EssayWorkPlaceConversation />}/>
                </Route>

                <Route path={userRequests}  >
                    <Route index element={<UserRequestList label="Обращения пользователей" target={userRequests}/>}/>
                    <Route path="get/:id" element={<UserRequestForm />}/>
                </Route>



                <Route path={essayWorkPlace}  >
                    <Route index element=
                        {
                            <EssayWorkPlaceList
                                label="Эссе (Рабочее пространство)"
                                bigLabel="Эссе"
                                target={essayWorkPlace}
                            />
                        }
                    />
                    <Route path="get/:id" element={<EssayForm />}/>
                </Route>


                <Route path={orders}  >
                    <Route index element={<OrderWorkPlaceList target={orders}/>}/>
                    <Route path={"get/:id"} element={<OrderWorkPlaceForm/>}/>
                </Route>


                    <Route path="panel" element={<Dashboard/>}/>
                    <Route path="dashboard" element={<Navigate to="/panel" replace/>}/>


                <Route path={articles}  >
                    <Route index element={<ArticleList target={articles}/>}/>
                    <Route path="get/:id" element={<ArticleForm/>}/>
                </Route>
                <Route path={tags}  >
                    <Route index element=
                        {
                            <TagList
                                label="Теги"
                                bigLabel="Тег"
                                target={tags}
                            />
                        }
                    />
                    <Route path="get/:id" element={<TagForm />}/>
                </Route>

                <Route path={categories}  >
                    <Route index element={<CategoryList  target={categories}/>}/>
                    <Route path="get/:id" element={<CategoryForm/>}/>
                </Route>
                <Route path={candidatetypes}  >
                    <Route index element=
                        {
                            <CandidateTypeList
                                label="Типы"
                                bigLabel="Тип"
                                target={candidatetypes}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="candidateTypes"/>}/>
                </Route>
                <Route path={videos}  >
                    <Route index element={<VideoList label="Видео" target={videos}/>}/>
                    <Route path="get/:id" element={<VideoForm  />}/>
                </Route>
                <Route path={locations}  >
                    <Route index element=
                        {
                            <LocationList
                                label="Локации"
                                bigLabel="Локацию"
                                target={locations}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="locations"/>}/>
                </Route>
                <Route path={programs}  >
                    <Route index element=
                        {
                            <ProgramList
                                label="Программы"
                                bigLabel="Программу"
                                target={programs}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="programs"/>}/>
                </Route>
                <Route path={roles}  >
                    <Route index element={
                        <RequireRole>
                            <RoleList
                                label="Роли"
                                bigLabel="Роль"
                                target={roles}
                            />
                        </RequireRole>
                    }/>
                    <Route path="get/:id" element={
                        <RequireRole>
                            <ResourceFormRoute resource="roles"/>
                        </RequireRole>
                    }/>
                </Route>
                <Route path={residences}  >
                    <Route index element=
                        {
                            <ResidenceList
                                label="Тип проживания"
                                bigLabel="Тип"
                                target={residences}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="residences"/>}/>
                </Route>
                <Route path={countries}  >
                    <Route index element=
                        {
                            <CountryList
                                label="Страны"
                                bigLabel="Страна"
                                target={countries}
                            />
                        }
                    />
                    <Route path="get/:id" element={<CountryForm/>}/>
                </Route>
                <Route path={candidates}  >
                    <Route index element={<CandidateList label="Кандидаты" target={candidates}/>}/>
                    <Route path="get/:id" element={<CandidateForm/>}/>
                </Route>
                <Route path={celebrities}  >
                    <Route index element={<CelebrityList label="Знаменитые выпускники" target={celebrities}/>}/>
                    <Route path="get/:id" element={<CelebrityForm/>}/>
                </Route>
                <Route path={genderschoolinformations}  >
                    <Route index element={<GenderInformationForSchoolList label="Пол" target={genderschoolinformations}/>}/>
                    <Route path="get/:id" element={<GenderInformationForSchoolForm/>}/>
                </Route>
                <Route path={schools}  >
                    <Route index element=
                        {
                            <SchoolList
                                   target={schools}
                            />
                        }
                    />
                    <Route path="get/:id" element={<SchoolForm/>}/>
                </Route>
                <Route path="typeschools">
                    <Route index element={<SchoolTypeList label="Типы школ"/>}/>
                    <Route path="get/:id" element={<ResourceFormRoute resource="schoolTypes"/>}/>
                </Route>
                <Route path={universities}  >
                    <Route index element=
                        {
                            <UniversityList
                                target={universities}
                            />
                        }
                    />
                    <Route path="get/:id" element={<UniversityForm/>}/>
                </Route>
                <Route path={universityPrepProgrammes}  >
                    <Route index element=
                        {
                            <UniversityPrepProgrammeList
                                target={universityPrepProgrammes}
                            />
                        }
                    />
                    <Route path="get/:id" element={<UniversityPrepProgrammeForm/>}/>
                </Route>
                <Route path={courses}  >
                    <Route index element=
                        {
                            <CourseList
                                target={courses}
                            />
                        }
                    />
                    <Route path="get/:id" element={<CourseForm/>}/>
                </Route>
                <Route path={faculties}  >
                    <Route index element=
                        {
                            <FacultyList
                                label="Факультеты"
                                bigLabel="Факультет"
                                target={faculties}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="faculties"/>}/>
                </Route>
                <Route path={clients}  >
                    <Route index element={<ClientList label="Клиенты" target={clients}/>}/>
                    <Route path="get/:id" element={<ClientForm />}/>
                </Route>
                <Route path={leads}  >
                    <Route index element={<RequestList label="Заявка" target={leads}/>}/>
                    <Route path="get/:id" element={<RequestForm />}/>
                </Route>
                <Route path={users}  >
                    <Route index element={
                        <RequireRole>
                            <UserList label="Пользователь" target={users}/>
                        </RequireRole>
                    }/>
                    <Route path="get/:id" element={
                        <RequireRole>
                            <UserForm/>
                        </RequireRole>
                    }/>
                </Route>
                <Route path={languageLevelTests}  >
                    <Route index element={<TestList/>}/>
                    <Route path="get/:id" element={<TestForm />}/>
                </Route>
                <Route path={onlinePlacementTests}  >
                    <Route index element={<OnlinePlacementTestList/>}/>
                    <Route path="get/:id" element={<OnlinePlacementTestForm />}/>
                </Route>
                <Route path={onlinePlacementTestQuestions}  >
                    <Route index element={<OnlinePlacementTestQuestionList label="Вопросы" target={onlinePlacementTestQuestions}/>}/>
                    <Route path="get/:id" element={<OnlinePlacementTestQuestionForm />}/>
                </Route>
                <Route path={testTypes}  >
                    <Route index element={<TestTypeList/>}/>
                    <Route path="get/:id" element={<ResourceFormRoute resource="testTypes"/>}/>
                </Route>
                <Route path={essays}  >
                    <Route index element={<EssayList label="Эссе" target={essays}/>}/>
                    <Route path="get/:id" element={<EssayForm />}/>
                </Route>
                <Route path={products}  >
                    <Route index element={<ProductList/>}/>
                    <Route path="get/:id" element={<ProductForm />}/>
                </Route>
                <Route path={globalSales}  >
                    <Route index element={<GlobalSaleList/>}/>
                    <Route path="get/:id" element={<GlobalSaleForm />}/>
                </Route>
                <Route path={mainCategoryTests}  >
                    <Route index element={<MainTestCategoryList/>}/>
                    <Route path="get/:id" element={<MainTestCategoryForm />}/>
                </Route>
                <Route path={cat4tests}  >
                    <Route index element={<Cat4TestList/>}/>
                    <Route path="get/:id" element={<Cat4TestForm />}/>
                </Route>
                <Route path={aiProducts}  >
                    <Route index element={<AiProductList/>}/>
                    <Route path="get/:id" element={<AiProductForm />}/>
                </Route>
                <Route path={packs}  >
                    <Route index element={<PackList/>}/>
                    <Route path="get/:id" element={<PackForm />}/>
                </Route>
                <Route path={samples}  >
                    <Route index element={<SampleList/>}/>
                    <Route path="get/:id" element={<SampleForm />}/>
                </Route>
                <Route path={mailingTypes}  >
                    <Route index element=
                        {
                            <MailingTypeList
                                label="Типы рассылок"
                                bigLabel="Тип"
                                target={mailingTypes}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="mailingTypes"/>}/>
                </Route>
                <Route path={testUsers}  >
                    <Route index element={<TestUserList label="Пользователи тестирования" target={testUsers}/>}/>
                    <Route path="get/:id" element={<TestUsersDetailed />}/>
                </Route>
                <Route path={requestPrices}  >
                    <Route index element=
                        {
                            <RequestPriceList
                                label="Консультации по ценам"
                                bigLabel="Консультацию"
                                target={requestPrices}
                            />
                        }
                    />
                    <Route path="get/:id" element={<RequestPriceDetailed />}/>
                </Route>
                <Route path={checks}  >
                    <Route index element=
                        {
                            <CheckList
                                label="Чеки"
                                bigLabel="Чек"
                                target={checks}
                            />
                        }
                    />
                    <Route path="get/:id" element={<CheckDetailed />}/>
                </Route>
                <Route path={testUsersInformation}  >
                    <Route index element=
                        {
                            <ResultPageForAdmins
                                label="Результаты пользователя"

                            />
                        }
                    />

                </Route>
                <Route path={results}  >
                    <Route index element=
                        {
                            <ResultsForAdmins
                                label="Результаты пользователей"

                            />
                        }
                    />
                    <Route path="cat4/get/:id" element={<Cat4ResultDetailed/>}/>
                    <Route path="languageLevel/get/:id" element={<LanguageLevelResultDetailed/>}/>

                </Route>
                <Route path={headers}  >
                    <Route index element=
                        {
                            <HeaderList
                                label="Header"
                                bigLabel="Header"
                                target={headers}
                            />
                        }
                    />
                    <Route path="get/:id" element={<HeaderFooterRoute kind="header"/>}/>
                </Route>

                <Route path={footers}  >
                    <Route index element=
                        {
                            <FooterList
                                label="Footer"
                                bigLabel="Footer"
                                target={footers}
                            />
                        }
                    />
                    <Route path="get/:id" element={<HeaderFooterRoute kind="footer"/>}/>
                </Route>
                <Route path={emailMessages}  >
                    <Route index element=
                        {
                            <EmailMessageList
                                label="Письма"
                                bigLabel="Письмо"
                                target={emailMessages}
                            />
                        }
                    />
                    <Route path="get/:id" element={<EmailMessageForm  />}/>
                </Route>
                <Route path={subscribers}  >
                    <Route index element=
                        {
                            <SubscriberList
                                label="Подписчики"
                                bigLabel="Подписчика"
                                target={subscribers}
                            />
                        }
                    />
                    <Route path="get/:id" element={<SubscriberForm  />}/>
                </Route>
                <Route path={subscriberList}  >
                    <Route index element=
                        {
                            <SubscriberListList
                                label="Список подписчиков"
                                bigLabel="Список"
                                target={subscriberList}
                            />
                        }
                    />
                    <Route path="get/:id" element={<SubscriberListFormRoute/>}/>
                </Route>
                <Route path={languages}  >
                    <Route index element=
                        {
                            <LanguageList
                                label="Список языков"
                                bigLabel="Язык"
                                target={languages}
                            />
                        }
                    />
                    <Route path="get/:id" element={<ResourceFormRoute resource="languages"/>}/>
                </Route>
                <Route path={mailings}  >
                    <Route index element=
                        {
                            <MailingList
                                label="Рассылки"
                                bigLabel="Рассылку"
                                target={mailings}
                            />
                        }
                    />
                    <Route path="get/:id" element={<MailingForm />}/>
                </Route>
                <Route path={maillogs}  >
                    <Route index element=
                        {
                            <MailLogList
                                label="Логи"
                                bigLabel="Логи"
                                target={maillogs}
                            />
                        }
                    />
                    <Route path="get/:id" element={<MailingForm />}/>
                </Route>
                <Route path={queue}  >
                    <Route index element=
                        {
                            <QueueList
                                label="Очередь"
                                bigLabel="Очередь"
                                target={queue}

                            />
                        }
                    />

                </Route>
                <Route path={queueHistory}  >
                    <Route index element=
                        {
                            <QueueHistoryList
                                label="История отправки"
                                bigLabel="Историю"
                                target={queueHistory}

                            />
                        }
                    />

                </Route>

                <Route path={sender}  >
                    <Route index element=
                        {
                            <SenderList
                                label="Отправители"
                                bigLabel="Отправителя"
                                target={sender}

                            />
                        }
                    />
                    <Route path="get/:id" element={<SenderForm />}/>
                </Route>
                <Route path="errors">
                    <Route index element={
                        <RequireRole>
                            <ErrorList/>
                        </RequireRole>
                    }/>
                    <Route path="get/:id" element={
                        <RequireRole>
                            <ErrorDetail/>
                        </RequireRole>
                    }/>
                </Route>
                <Route path="notificationLeads">
                    <Route index element={
                        <RequireRole>
                            <NotificationLeadList/>
                        </RequireRole>
                    }/>
                    <Route path="get/:id" element={
                        <RequireRole>
                            <NotificationLeadDetail/>
                        </RequireRole>
                    }/>
                </Route>
                <Route path="chats">
                    <Route index element={
                        <RequireRole>
                            <ChatArchiveList/>
                        </RequireRole>
                    }/>
                    <Route path="get/:id" element={
                        <RequireRole>
                            <ChatArchiveDetail/>
                        </RequireRole>
                    }/>
                    <Route path="banned" element={
                        <RequireRole>
                            <ChatBannedList/>
                        </RequireRole>
                    }/>
                    <Route path="prompts" element={
                        <RequireRole>
                            <ChatPrompts/>
                        </RequireRole>
                    }/>
                </Route>
                <Route path="offlineConversions" element={
                    <RequireRole>
                        <OfflineConversionsPage/>
                    </RequireRole>
                }/>
                <Route path="banners" element={
                    <RequireRole>
                        <Banners/>
                    </RequireRole>
                }/>
                <Route path={files} element={<FilePageUpload/>}  ></Route>

            </Route>
        </Route>

        <Route path="*" element={<Navigate to="/panel" />} />
    </Route>
);

export const router = createBrowserRouter(createRoutesFromElements(routeTree));

const App = () => (
    <Suspense fallback={<Loader/>}>
        <RouterProvider router={router}/>
    </Suspense>
);

export default App;
